import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

// Carte Stripe Connect pour les livreurs Nold Go (Europe).
// - Statut coloré (déconnecté / à finaliser / actif)
// - Bouton "Connecter mon compte Stripe" → redirige vers l'onboarding hébergé Stripe
// - Bouton "Ouvrir mon tableau de bord Stripe" une fois connecté
export default function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/driver/stripe-connect/status");
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false, error: e?.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Si le livreur revient de l'onboarding Stripe (?stripe_connect=done), on refresh
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_connect")) {
      // Nettoie l'URL et recharge le statut
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(load, 800);
    }
  }, []);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/driver/stripe-connect/onboard", {});
      window.location.assign(data.onboarding_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir Stripe. Réessayez.");
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/driver/stripe-connect/dashboard-link");
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir le tableau de bord.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="stripe-connect-loading" className="mt-5 rounded-3xl p-5 bg-white border border-[#E2DFD8] text-sm text-[#75807A]">
        Chargement Stripe Connect…
      </div>
    );
  }

  const share = status?.share_pct || 75;
  const connected = !!status?.connected;
  const submitted = !!status?.details_submitted;
  const payoutsOn = !!status?.payouts_enabled;

  // 3 états : pas connecté / onboarding en cours / actif
  if (!connected) {
    return (
      <div data-testid="stripe-connect-card" className="mt-5 rounded-3xl p-5 bg-gradient-to-br from-[#635BFF] to-[#4B45C7] text-white shadow-[0_8px_30px_rgba(99,91,255,0.3)]">
        <div className="flex items-start gap-3">
          <div className="text-3xl">💳</div>
          <div className="flex-1">
            <div className="font-head font-black text-lg">Payez-vous automatiquement avec Stripe</div>
            <p className="text-sm text-white/90 mt-1 leading-relaxed">
              Connectez votre compte Stripe et recevez <b>{share}% de chaque course</b> directement
              sur votre compte bancaire — Stripe gère la sécurité, le KYC et les payouts.
            </p>
            <button
              data-testid="stripe-connect-start-btn"
              onClick={startOnboarding}
              disabled={busy}
              className="mt-4 h-11 px-5 rounded-xl bg-white text-[#635BFF] font-black text-sm hover:bg-white/95 disabled:opacity-40 inline-flex items-center gap-2"
            >
              {busy ? "Ouverture de Stripe…" : "Connecter mon compte Stripe →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Connecté mais onboarding pas terminé
  if (!submitted || !payoutsOn) {
    return (
      <div data-testid="stripe-connect-card" className="mt-5 rounded-3xl p-5 bg-[#FEF3C7] border-2 border-[#F59E0B]">
        <div className="flex items-start gap-3">
          <div className="text-2xl">⏳</div>
          <div className="flex-1">
            <div className="font-head font-black text-base text-[#92400E]">Onboarding Stripe à finaliser</div>
            <p className="text-sm text-[#78350F] mt-1">
              Il reste des informations à saisir chez Stripe pour activer vos payouts.
              {status?.requirements_due?.length ? ` À compléter : ${status.requirements_due.slice(0, 3).join(", ")}${status.requirements_due.length > 3 ? "…" : ""}` : ""}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                data-testid="stripe-connect-continue-btn"
                onClick={startOnboarding}
                disabled={busy}
                className="h-10 px-4 rounded-lg bg-[#F59E0B] text-white font-bold text-sm hover:bg-[#D97706] disabled:opacity-40">
                {busy ? "…" : "Reprendre l'onboarding Stripe →"}
              </button>
              <button
                data-testid="stripe-connect-dashboard-btn-partial"
                onClick={openDashboard}
                disabled={busy}
                className="h-10 px-4 rounded-lg bg-white border-2 border-[#F59E0B] text-[#B45309] font-bold text-sm hover:bg-[#FEF3C7] disabled:opacity-40">
                Ouvrir mon compte Stripe
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Compte actif
  return (
    <div data-testid="stripe-connect-card" className="mt-5 rounded-3xl p-5 bg-white border-2 border-[#16a34a]">
      <div className="flex items-start gap-3">
        <div className="text-2xl">✅</div>
        <div className="flex-1">
          <div className="font-head font-black text-base text-[#1A1A1A]">Stripe Connect actif</div>
          <p className="text-sm text-[#3B423E] mt-1">
            Vous recevez <b className="text-[#16a34a]">{share}%</b> de chaque course
            directement sur votre compte Stripe. Consultez vos paiements, changez votre RIB
            ou demandez un instant payout depuis votre tableau de bord Stripe.
          </p>
          <button
            data-testid="stripe-connect-dashboard-btn"
            onClick={openDashboard}
            disabled={busy}
            className="mt-3 h-10 px-4 rounded-lg bg-[#635BFF] hover:bg-[#4B45C7] text-white font-bold text-sm disabled:opacity-40 inline-flex items-center gap-2">
            {busy ? "…" : "Ouvrir mon tableau de bord Stripe →"}
          </button>
        </div>
      </div>
    </div>
  );
}
